import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, ShieldCheck, Loader2, Paperclip, X, FileText } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { EmergencyNotice } from "./EmergencyNotice";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "ABA Therapy",
  "Speech Therapy",
  "Physical Therapy",
  "Occupational Therapy",
];

const DIAGNOSIS_OPTIONS = ["Yes", "No", "Not sure"];

const mapDefaultService = (s = "") => {
  const v = s.toLowerCase();
  if (v.includes("aba") || v.includes("behavior")) return ["ABA Therapy"];
  if (v.includes("speech")) return ["Speech Therapy"];
  if (v.includes("physical")) return ["Physical Therapy"];
  if (v.includes("occupational")) return ["Occupational Therapy"];
  return [];
};

const empty = {
  name: "",
  phone: "",
  email: "",
  preferred_contact: "email",
  services_interested: [],
  autism_diagnosis: "",
  message: "",
};

export const ContactForm = ({ defaultService = "" }) => {
  const { t } = useLang();
  const [form, setForm] = useState({
    ...empty,
    services_interested: mapDefaultService(defaultService),
  });
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (attachments.length + files.length > 5) {
      toast.error(t("You can attach up to 5 files."));
      return;
    }
    setUploading(true);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} ${t("is larger than 10MB.")}`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((a) => [...a, data]);
      } catch (err) {
        toast.error(
          err.response?.status === 400
            ? `${file.name}: ${t("unsupported type or too large.")}`
            : `${t("Could not upload")} ${file.name}.`
        );
      }
    }
    setUploading(false);
  };

  const removeAttachment = (id) =>
    setAttachments((a) => a.filter((f) => f.id !== id));

  const isABA = form.services_interested.includes("ABA Therapy");

  const toggleService = (name) =>
    setForm((f) => {
      const has = f.services_interested.includes(name);
      const services_interested = has
        ? f.services_interested.filter((x) => x !== name)
        : [...f.services_interested, name];
      return {
        ...f,
        services_interested,
        autism_diagnosis:
          name === "ABA Therapy" && has ? "" : f.autism_diagnosis,
      };
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error(t("Please add your name and a short message."));
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error(t("Please provide either an email or a phone number."));
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, attachments };
      if (!payload.email) delete payload.email;
      if (!isABA) payload.autism_diagnosis = "";
      await axios.post(`${API}/contact`, payload);
      toast.success(t("Thank you — your request has been received. We'll be in touch soon."));
      setForm({ ...empty, services_interested: mapDefaultService(defaultService) });
      setAttachments([]);
    } catch (err) {
      toast.error(t("Something went wrong. Please try again or call us directly."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="cf-name">{t("Full name")} <span className="text-destructive">*</span></Label>
          <Input
            id="cf-name"
            data-testid="contact-name-input"
            value={form.name}
            onChange={set("name")}
            placeholder={t("Your name")}
            autoComplete="name"
            required
            className="h-12 rounded-xl bg-white"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cf-phone">{t("Phone")}</Label>
          <Input
            id="cf-phone"
            data-testid="contact-phone-input"
            value={form.phone}
            onChange={set("phone")}
            placeholder="(555) 123-4567"
            autoComplete="tel"
            inputMode="tel"
            className="h-12 rounded-xl bg-white"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cf-email">{t("Email")}</Label>
        <Input
          id="cf-email"
          data-testid="contact-email-input"
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="you@example.com"
          autoComplete="email"
          className="h-12 rounded-xl bg-white"
        />
      </div>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium mb-1">
          {t("Select all services that your family is interested in")}
        </legend>
        <div className="grid sm:grid-cols-2 gap-3">
          {SERVICE_OPTIONS.map((opt) => {
            const checked = form.services_interested.includes(opt);
            return (
              <label
                key={opt}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm cursor-pointer transition-colors ${
                  checked
                    ? "border-teal bg-teal/10 text-teal"
                    : "border-input text-ink/75 hover:border-teal/40"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleService(opt)}
                  data-testid={`contact-service-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                  className="h-4 w-4 rounded border-input accent-teal"
                />
                {t(opt)}
              </label>
            );
          })}
        </div>
      </fieldset>

      {isABA && (
        <fieldset
          data-testid="aba-diagnosis-group"
          className="space-y-2 rounded-2xl border border-teal/25 bg-teal/[0.04] p-4"
        >
          <legend className="text-sm font-medium px-1">
            {t("(ABA only) Has your child received an official autism diagnosis?")}
          </legend>
          <div className="flex flex-wrap gap-3">
            {DIAGNOSIS_OPTIONS.map((opt) => (
              <label
                key={opt}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm cursor-pointer transition-colors ${
                  form.autism_diagnosis === opt
                    ? "border-teal bg-teal/10 text-teal"
                    : "border-input text-ink/70 hover:border-teal/40"
                }`}
              >
                <input
                  type="radio"
                  name="autism_diagnosis"
                  value={opt}
                  checked={form.autism_diagnosis === opt}
                  onChange={set("autism_diagnosis")}
                  data-testid={`contact-diagnosis-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                  className="sr-only"
                />
                {t(opt)}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium mb-1">{t("Preferred contact method")}</legend>
        <div className="flex flex-wrap gap-3">
          {["email", "phone", "either"].map((opt) => (
            <label
              key={opt}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm cursor-pointer transition-colors ${
                form.preferred_contact === opt
                  ? "border-teal bg-teal/10 text-teal"
                  : "border-input text-ink/70 hover:border-teal/40"
              }`}
            >
              <input
                type="radio"
                name="preferred_contact"
                value={opt}
                checked={form.preferred_contact === opt}
                onChange={set("preferred_contact")}
                data-testid={`contact-pref-${opt}`}
                className="sr-only"
              />
              <span className="capitalize">{t(opt)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="space-y-2">
        <Label htmlFor="cf-message">{t("How can we help?")} <span className="text-destructive">*</span></Label>
        <Textarea
          id="cf-message"
          data-testid="contact-message-input"
          value={form.message}
          onChange={set("message")}
          placeholder={t("Share a little about what you're looking for. Please do not include private medical information.")}
          rows={5}
          required
          className="rounded-xl bg-white resize-none"
        />
      </div>

      <div className="space-y-3">
        <Label>{t("Attach Referral")} <span className="text-ink/40 font-normal">{t("(optional)")}</span></Label>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.gif,.webp,.heic,.pdf,.doc,.docx,.txt,image/*"
          onChange={handleFiles}
          data-testid="contact-file-input"
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || attachments.length >= 5}
          data-testid="contact-attach-btn"
          className="inline-flex items-center gap-2 rounded-xl border border-dashed border-teal/40 bg-white px-4 py-3 text-sm text-ink/70 hover:border-teal hover:text-teal transition-colors disabled:opacity-50 w-full justify-center"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
          {uploading ? t("Uploading…") : t("Choose files to upload")}
        </button>
        <p className="text-xs text-ink/45">{t("Images or documents, up to 5 files, 10MB each.")}</p>

        {attachments.length > 0 && (
          <ul data-testid="contact-attachment-list" className="space-y-2">
            {attachments.map((f) => (
              <li
                key={f.id}
                data-testid={`attachment-${f.id}`}
                className="flex items-center gap-3 rounded-xl border border-black/5 bg-white px-3 py-2.5"
              >
                <FileText size={16} className="text-teal shrink-0" />
                <span className="flex-1 min-w-0 truncate text-sm text-ink/80">{f.filename}</span>
                <span className="text-xs text-ink/40">{Math.max(1, Math.round(f.size / 1024))} KB</span>
                <button
                  type="button"
                  onClick={() => removeAttachment(f.id)}
                  aria-label={`${t("Remove")} ${f.filename}`}
                  data-testid={`remove-attachment-${f.id}`}
                  className="grid place-items-center h-7 w-7 rounded-full text-ink/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <X size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-start gap-2.5 rounded-xl bg-muted px-4 py-3">
        <ShieldCheck size={18} className="text-teal shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-xs text-ink/70 leading-relaxed">
          {t("For your privacy, please do not submit private medical information through this form. We'll follow up to gather any details securely.")}
        </p>
      </div>

      <button
        type="submit"
        data-testid="contact-submit-btn"
        disabled={loading}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-teal px-7 py-3.5 font-medium text-white hover:bg-[#2E6088] transition-colors duration-200 disabled:opacity-60 w-full sm:w-auto"
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        {loading ? t("Sending…") : t("Request Information")}
      </button>

      <EmergencyNotice className="mt-2" />
    </form>
  );
};
